import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { combineLatest, distinctUntilChanged, filter, tap } from 'rxjs';

import {
  AnalyticsEvent,
  EcommerceDataKeys,
  Ga4TrackingEventData,
  Ga4TrackingEventName,
  NavigationItems,
  PageViewType,
  PromoViewClickKey,
  PromotionDataKeys,
  RouteData,
  TrackingContentData,
  TrackingData,
  TrackingEcommerceEventData,
  TrackingEventData,
  TrackingPageViewData,
  TrackingPromotionData,
  TrackingPromotionEventData
} from '@hdms/shared/common-types';

import { EnvironmentService } from '../environment/environment.service';
import { removeHash, sanitizeUrl, urlAfterRedirectsEqual } from '../helpers';
import { PlatformService } from '../platform/platform.service';
import { StorageService, StorageType } from '../storage/storage.service';
import { WindowRefService } from '../window-ref/window-ref.service';

import {
  createDataLayerEvent,
  createDataLayerPageView,
  getBaseTrackingData,
  getRouteData,
  prepareTrackingData
} from './analytics.helper';
import { BaseTrackingDto } from './base-tracking.dto';
import { MPulseTrackingService } from './mpulse-tracking.service';

// HDP-61940
export const DEFAULT_NAVIGATION_DATA: TrackingContentData = {
  navigationItem: NavigationItems.OTHER,
  navigationItemDetails: 'Other'
};
export const TRACKING_STORAGE_NAME = 'dpiTrackingData';
const PAGE_TEMPALTE = 'pageTemplate';
const PAGE_PATH = 'pagePath';

// Used to store the page navigation details for Full mode routes
export const NAVIGATION_DATA = 'navigationDetails';

type DataLayer = (TrackingPageViewData | TrackingEventData | Ga4TrackingEventData<Ga4TrackingEventName>)[];

interface AnalyticsWindow extends Window {
  dataLayer: DataLayer;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  // Indicates if tracking is active based on `window.dataLayer` presence
  public active: boolean;

  private previousPage: string;

  private readonly dataLayer: DataLayer;
  private readonly baseTrackingData$ = this.baseTrackingDto.update$().pipe(tap(data => (this.baseTrackingData = data)));
  private readonly pageTrackingEnabled: boolean;
  private baseTrackingData: TrackingData = {};
  private firstRequest = true;
  // temporary stored route data
  private currentRouteData: RouteData;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly storageService: StorageService,
    private readonly baseTrackingDto: BaseTrackingDto,
    private readonly mPulseService: MPulseTrackingService,
    { nativeWindow }: WindowRefService<AnalyticsWindow>,
    { pageFrameOnly }: EnvironmentService,
    { isBrowser }: PlatformService
  ) {
    this.dataLayer = nativeWindow.dataLayer;
    this.active = !!this.dataLayer;
    // Disable page tracking when app doesn't serve page content
    this.pageTrackingEnabled = this.active && !pageFrameOnly && isBrowser;

    this.pageTrackingEnabled && this.initRouterSubscription();
  }

  /**
   * Invokes a virtualPageView tracking request
   *
   * @param url the url to be tracked
   * @param trackingData optional, will overwrite existing global / route tracking data
   * @param allowEqualUrl
   */
  public trackPageView(url: string, trackingData?: TrackingData, allowEqualUrl = false): void {
    if (!this.pageTrackingEnabled || !url) {
      return;
    }

    if (this.previousPage !== url || allowEqualUrl) {
      trackingData = createDataLayerPageView(removeHash(url), {
        ...this.getAvailableTrackingData(),
        ...trackingData
      });

      this.pushToDataLayer(trackingData);
      this.updateMPulseData(trackingData);

      this.previousPage = url;
    }
  }

  public trackEcommerceEvent(ecommerceEvent: TrackingEcommerceEventData, omitInitialTrackingData = true): void {
    const { event, currencyCode, products, ...trackingData } = ecommerceEvent || {};
    const productsKey = EcommerceDataKeys[event];

    if (!event || !products?.length || !productsKey) {
      return;
    }

    const eventData = {
      event,
      ecommerce: {
        currencyCode,
        [productsKey]: { products }
      },
      ...trackingData
    };
    this.sendEvent(eventData, omitInitialTrackingData);
  }

  public trackPromotionEvent(event: PromotionDataKeys, promotions: TrackingPromotionData[]): void {
    const eventData: TrackingPromotionEventData = {
      event,
      ecommerce: {
        [PromoViewClickKey[event]]: {
          promotions
        }
      }
    };
    this.sendEvent(eventData);
  }

  /**
   * Invokes a virtualPageView tracking request including Previous Page Data
   * Triggered transparently via router event subscription
   *
   * @param url the url to be tracked
   * @param trackingData additional data to be tracked
   * @param allowEqualUrl default false, set to true if tracking needed for same url path
   */
  public trackPageViewWithPrevPageData(url: string, trackingData?: TrackingData, allowEqualUrl = false): void {
    this.trackPageView(url, { ...trackingData, ...this.getPreviousPageTrackData(url) }, allowEqualUrl);
  }

  /**
   * Send event triggered by user interaction
   *
   * @param event AnalyticsEvent
   * @param omitInitialTrackingData boolean
   */
  public sendEvent(event: AnalyticsEvent | Ga4TrackingEventData<never>, omitInitialTrackingData = true): void {
    if (!this.active || !event) {
      return;
    }

    omitInitialTrackingData
      ? this.pushToDataLayer(createDataLayerEvent(event))
      : this.pushToDataLayer(createDataLayerEvent(event, this.getAvailableTrackingData()));
  }

  /**
   * Store navigation info to SessionStorage
   *
   * @param data AnalyticsEvent
   * @param storageName string
   */
  public storeData(data: AnalyticsEvent, storageName = TRACKING_STORAGE_NAME): void {
    this.storageService.setItem(storageName, data, StorageType.SESSION, true);
  }

  private initRouterSubscription(): void {
    combineLatest([
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged(urlAfterRedirectsEqual)
      ),
      this.baseTrackingData$
    ]).subscribe(([{ urlAfterRedirects }]) => {
      this.currentRouteData = this.getCurrentRouteData();
      this.trackPageViewWithPrevPageData(urlAfterRedirects);
      this.currentRouteData = null;
    });
  }

  private getRouteTrackingData(): TrackingData {
    const routeData = getRouteData(this.activatedRoute);
    return {
      ...routeData.tracking,
      ...this.getInitialTrackingData(routeData)
    };
  }

  private getInitialTrackingData(data: RouteData): TrackingData {
    if (data.initialTracking) {
      const initialTrackingData = { ...DEFAULT_NAVIGATION_DATA, ...data.initialTracking };
      delete data.initialTracking;
      return initialTrackingData;
    }
    return DEFAULT_NAVIGATION_DATA;
  }

  private getAvailableTrackingData(): TrackingData {
    return {
      ...this.updatePageViewType(getBaseTrackingData(this.baseTrackingData)),
      ...this.getRouteTrackingData()
    };
  }

  private getPreviousPageTrackData(url: string): TrackingData {
    const previousPageTrackData: TrackingData = {};

    const prevPageTemplate = this.storageService.getItem(PAGE_TEMPALTE, StorageType.SESSION);
    const prevPagePath = this.storageService.getItem(PAGE_PATH, StorageType.SESSION);

    if (prevPageTemplate) {
      previousPageTrackData.previousTemplateType = prevPageTemplate;
      previousPageTrackData.previousPagePath = prevPagePath;
    }

    // Get the navigation details from the previous step
    const navigationDetails: TrackingContentData = this.storageService.getItem(NAVIGATION_DATA, StorageType.SESSION);
    if (navigationDetails) {
      previousPageTrackData.navigationItem = navigationDetails.navigationItem;
      previousPageTrackData.navigationItemDetails = navigationDetails.navigationItemDetails;

      // Remove the navigation object from session storage so that it wont be added during further navigation unless populated
      this.storageService.removeItem(NAVIGATION_DATA, StorageType.SESSION);
    }

    if (!this.baseTrackingData.templateType) {
      // in case of standalone scenario, templateType won't be provided by initial base tracking data
      // so let's take it from router data config, if present.
      this.baseTrackingData.templateType = this.getCurrentRouteData().tracking?.templateType;
    }

    this.storageService.setItem(PAGE_TEMPALTE, this.baseTrackingData.templateType, StorageType.SESSION);
    this.storageService.setItem(PAGE_PATH, sanitizeUrl(url), StorageType.SESSION); // exclude extra params in the URL using sanitizeUrl

    return previousPageTrackData;
  }

  /**
   * `pageViewType` is either `user_initiated` or `system_initiated`.
   * As long base tracking data is provided by Hybris, for the first request
   * always the provided value has to be taken, afterwards it's set to `user_initiated`.
   *
   * TODO: For subsequent requests the value has to be set depending if the active route
   *  was a result of a system-initiated (Angular router internal) redirect or not.
   *  This could happen if user was logged out automatically after session expiry.
   */
  private updatePageViewType(trackingData: TrackingData): TrackingData {
    if (!this.firstRequest) {
      trackingData.pageviewType = PageViewType.USER_INITIATED;
    }
    this.firstRequest = false;

    return trackingData;
  }

  private pushToDataLayer(data: TrackingData): void {
    this.dataLayer.push(prepareTrackingData(data));
  }

  private getCurrentRouteData(): RouteData {
    return this.currentRouteData || getRouteData(this.activatedRoute);
  }

  private updateMPulseData(trackingData?: TrackingData): void {
    if (trackingData) {
      const { templateType, productCategory } = trackingData;
      this.mPulseService.updateData({
        templateType,
        productCategory
      });
    }
  }
}
